import { Backdrop, CircularProgress, CssBaseline } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { formDevtoolsPlugin } from '@tanstack/react-form-devtools';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { useEffect, useMemo, useState } from 'react';
import toast, { Toaster, useToasterStore } from 'react-hot-toast';

import { type RootState, useSelector as useReduxSelector } from '~/redux/store';

import LocalStorageService from '~/services/localStorage.service';

import { VestigasDevtools } from '~/modules/devtools';
import { UpdatePrompt } from '~/modules/pwa';
import { RoutingProgressIndicator } from '~/modules/router';

import { ItemCollectionNavigation } from '~/utils/useItemCollection';

import { MainNav } from '~/components/menu/MainNav';
import { TopBar } from '~/components/menu/TopBar';
import { useMainNavItems } from '~/components/menu/useMainNavItems';

const selectBackdrop = createSelector(
  [(state: RootState) => state.backdrop],
  (backdrop) => ({
    message: backdrop.message,
    open: backdrop.open,
  }),
);

function RootLayout() {
  const location = useLocation();

  const backdrop = useReduxSelector(selectBackdrop);

  // null = no stored preference yet; only then the responsive default applies,
  // so the expand button also works while the window is small.
  const [isNavCollapsed, setIsNavCollapsed] = useState<boolean | null>(() =>
    LocalStorageService.getLocalStorage(LocalStorageService.HIDE_DRAWER) == null
      ? null
      : LocalStorageService.getBooleanFromLocalStorage(
          LocalStorageService.HIDE_DRAWER,
        ),
  );
  const [shouldNavBeCollapsed, setShouldNavBeCollapsed] = useState(false);

  useEffect(() => {
    // For small screens, we collapse the nav by default w/o overriding the user setting.
    const handleResize = () => {
      setShouldNavBeCollapsed(window.innerWidth <= 1024);
    };

    handleResize();

    // TODO: provide custom hook with throttled resize observer
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const drawerWidthOpen = 288;
  const drawerWidthClosed = 110;
  const drawerWidth = useMemo(() => {
    return (isNavCollapsed ?? shouldNavBeCollapsed)
      ? drawerWidthClosed
      : drawerWidthOpen;
  }, [shouldNavBeCollapsed, isNavCollapsed]);

  const mainNavItems = useMainNavItems();

  // Enforce limit of toasts.
  const { toasts } = useToasterStore();
  useEffect(() => {
    for (const t of toasts
      .filter((t) => t.visible) // Only consider visible toasts
      .filter((_, index) => index >= 5)) {
      toast.dismiss(t.id); // Dismiss – Use toast.remove(t.id) removal without animation
    }
  }, [toasts]);

  return (
    <div className="relative flex h-screen w-screen">
      <RoutingProgressIndicator key={location.pathname} />
      <Backdrop
        open={Boolean(backdrop.open)}
        sx={{
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: '#fff',
          textShadow: '0 0 0.75vw black',
          transitionProperty: 'opacity',
          transitionTimingFunction: 'ease-out',
          zIndex: (theme) => theme.zIndex.snackbar,
        }}
      >
        <CircularProgress className="mr-4" color="inherit" />
        {backdrop.message}
      </Backdrop>
      <Toaster
        toastOptions={{ className: 'rounded-md max-w-[60vw] py-1 pl-4' }}
      />
      <CssBaseline />
      <TopBar drawerWidth={drawerWidth} />
      <MainNav
        drawerWidth={drawerWidth}
        isNavCollapsed={isNavCollapsed ?? shouldNavBeCollapsed}
        items={mainNavItems}
        setIsNavCollapsed={setIsNavCollapsed}
      />
      <main
        className="will-change-width flex-1 overflow-auto"
        style={{
          height: '100%',
          // Offsets the fixed TopBar as padding so content scrolls beneath it,
          // which the bar's backdrop blur needs in order to have any effect.
          paddingTop: '63px',
          scrollbarGutter: 'stable',
          width: `calc(100% - ${drawerWidth}px)`,
        }}
      >
        <Outlet />
        <TanStackDevtools
          config={{
            defaultOpen: false,
            // openHotkey: ['meta', '.'],
            panelLocation: 'bottom',
            port: 8090,
            position: 'bottom-left',
          }}
          plugins={[
            {
              name: 'TanStack Query',
              render: <ReactQueryDevtoolsPanel />,
            },
            {
              name: 'TanStack Router',
              render: <TanStackRouterDevtoolsPanel />,
            },
            {
              name: 'Vestigas Devtools',
              render: <VestigasDevtools />,
            },
            formDevtoolsPlugin(),
          ]}
        />
      </main>
      <ItemCollectionNavigation className="fixed bottom-0 left-1/2 z-[1400] -translate-x-1/2" />
      <UpdatePrompt />
    </div>
  );
}

export const Route = createFileRoute('/(main)')({
  component: RootLayout,
});
